import styled from "styled-components";
import {
  QUERIES,
  SPACING,
  SPACING_RESPONSIVE,
} from "../../../../../../utils/constants";
import { StatusVariant } from "../types";

const STATUS_COLOR: Record<StatusVariant, string> = {
  active: "var(--color-success)",
  completed: "var(--color-info)",
  defaulted: "var(--color-error)",
  inactive: "var(--color-text-muted)",
};

export const SummaryCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.lg}px;
  min-width: 0;
  padding: ${SPACING_RESPONSIVE.mobile.cardPadding}px;
  background: var(--hero-background);
  border: 1px solid var(--color-card-border);
  border-radius: 16px;
  box-shadow: var(--shadow-small);

  @media ${QUERIES.tabletLargeAndUp} {
    padding: ${SPACING_RESPONSIVE.tablet.cardPadding}px;
  }

  @media ${QUERIES.laptopAndUp} {
    padding: ${SPACING_RESPONSIVE.desktop.cardPadding}px;
    background: transparent;
    border: none;
    border-radius: 0;
    box-shadow: none;
  }
`;

export const SummaryHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
`;

export const KebabWrapper = styled.div`
  flex-shrink: 0;

  @media ${QUERIES.laptopAndUp} {
    display: none;
  }
`;

export const ActionsWrapper = styled.div`
  display: none;

  @media ${QUERIES.laptopAndUp} {
    display: block;
    flex-shrink: 0;
  }
`;

export const IdentityBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
  min-width: 0;
`;

export const TitleRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;

  @media ${QUERIES.laptopAndUp} {
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: ${SPACING.md}px;
  }
`;

export const HeroName = styled.h1`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-display);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.1;
  color: var(--color-text-default);
  margin: 0;
`;

export const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  flex-wrap: wrap;

  @media ${QUERIES.laptopAndUp} {
    flex-direction: row-reverse;
  }
`;

export const StatusBadge = styled.span<{ $variant: StatusVariant }>`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.xs}px;
  padding: ${SPACING.xs}px ${SPACING.sm}px;
  border-radius: 999px;
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ $variant }) => STATUS_COLOR[$variant]};
  background-color: ${({ $variant }) =>
    `color-mix(in srgb, ${STATUS_COLOR[$variant]} 14%, transparent)`};
  border: 1px solid
    ${({ $variant }) =>
      `color-mix(in srgb, ${STATUS_COLOR[$variant]} 30%, transparent)`};

  &::before {
    content: "";
    width: 0.4em;
    height: 0.4em;
    border-radius: 50%;
    background-color: currentColor;
  }
`;

export const TypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.xs}px;
  padding: ${SPACING.xs}px ${SPACING.sm}px;
  border-radius: 999px;
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
  background-color: var(--color-gray-pill);
  border: 1px solid var(--color-card-border);

  svg {
    font-size: var(--text-md);
  }
`;

export const LastPaymentText = styled.span`
  font-size: var(--text-xs);
  color: var(--color-text-muted);
  letter-spacing: 0.02em;
`;

export const LastPaymentPrefixLong = styled.span`
  display: none;

  @media ${QUERIES.laptopAndUp} {
    display: inline;
  }
`;

export const LastPaymentPrefixShort = styled.span`
  display: inline;

  @media ${QUERIES.laptopAndUp} {
    display: none;
  }
`;

export const BalanceBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
`;

export const BalanceLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

export const BalanceValue = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-display-xl);
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1;
  color: var(--color-text-default);
`;

export const ProgressBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
`;

export const ProgressMeta = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
  flex-wrap: wrap;
`;

export const ProgressPaid = styled.span`
  font-size: var(--text-base);
  font-weight: 700;
  color: var(--color-primary);
`;

export const ProgressAmounts = styled.span`
  font-size: var(--text-sm);
  color: var(--color-text-secondary);

  @media ${QUERIES.laptopAndUp} {
    display: none;
  }
`;

export const ProgressToGo = styled.span`
  display: none;
  font-size: var(--text-sm);
  color: var(--color-text-secondary);

  @media ${QUERIES.laptopAndUp} {
    display: inline;
  }
`;

export const ProgressTrack = styled.div`
  position: relative;
  height: 8px;
  border-radius: 999px;
  background-color: var(--color-border);
  overflow: hidden;
`;

export const ProgressFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => `${$pct}%`};
  border-radius: 999px;
  background-color: var(--color-primary);
`;

export const DetailsPanel = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${SPACING.lg}px;
  padding: ${SPACING.lg}px;
  background: var(--color-card-background);
  border: 1px solid var(--color-card-border);
  border-radius: 10px;

  @media ${QUERIES.laptopAndUp} {
    display: none;
  }
`;

export const DetailBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xxs}px;
  min-width: 0;
`;

export const DetailLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-text-secondary);
`;

export const DetailValue = styled.span<{ $accent?: boolean }>`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-lg);
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ $accent }) =>
    $accent ? "var(--color-primary)" : "var(--color-text-default)"};
  word-break: break-word;
`;

export const DetailSub = styled.span`
  font-size: var(--text-xs);
  font-style: italic;
  color: var(--color-text-secondary);
`;
